import { uniq } from 'lodash';

import {
  FREE_SHIPPING_AMOUNT_TEXT,
  ZIGZIN_PRODUCT_FREE_SHIPPING_AMOUNT,
  ZIGZIN_PRODUCT_FREE_SHIPPING_AMOUNT_TEXT,
} from '../constant/price-summary';
import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matching_catalog_product_info_discount_info as MatchingProductDiscountInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_price_coupon_discount_info_list as ProductPriceCouponDiscountInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_price_first_order_discount as FirstOrderDiscount,
  PdpCouponDiscountType,
  PdpShippingFeeType,
} from '../graphql/select-option_types.2';
import type { ItemCardType, OptionInfo, ProductDiscountInfo, ZigzinShippingBaseInfo } from '../type/common.type';
import { checkZigzinOrVendorFlexDeliveryType } from '../util/checkers/zigzin-checker';
import { checkZigzinOrVendorFlexFulfillmentType } from '../util/checkers/zigzin-checker';
import {
  asCurrency,
  bpToDecimal,
  ceilTens,
  getNonNegativeValue,
  limitToMax,
} from '../util/transformers/number-transformer';

export interface ProductPriceCalculatorParams {
  /** 쿠폰 사용 안함 여부 */
  disabledCoupon?: boolean;
  /** 옵션 데이터 정보 */
  option_data_info: OptionInfo | null;
  /** 아이템 카드 리스트 */
  item_card_list: ItemCardType[];
  /** 직진 배송 정보 */
  zigzin_shipping_base_info: ZigzinShippingBaseInfo | null;
}

interface ShippingDisplay {
  mode: 'hidden' | 'text' | 'mixed';
  text?: string;
  left_name?: string;
  left_value?: string;
  right_name?: string;
  right_value?: string;
}

type PriceList = { price: number; count: number }[];

interface TotalInfoProps {
  count: number;
  price: number;
  final_price: number;
  price_list: PriceList;
  general_price: number;
  zigzin_price_list: PriceList;
  zigzin_count: number;
  zigzin_price: number;
}

interface DiscountedPriceProps {
  price_list: PriceList;
  total_price: number;
  discount_info?: MatchingProductDiscountInfo | ProductDiscountInfo | null;
  coupon_discount_info?: ProductPriceCouponDiscountInfo | null;
  first_order_discount?: FirstOrderDiscount | null;
  disabledCoupon?: boolean;
}

/**
 * 상품 가격 계산
 * @param disabledCoupon - 쿠폰 사용 여부
 * @param option_data_info - 옵션 데이터 정보
 * @param item_card_list - 아이템 리스트
 * @returns 상품 가격 정보
 */
export const productPriceCalculator = ({
  disabledCoupon,
  option_data_info,
  item_card_list = [],
  zigzin_shipping_base_info,
}: ProductPriceCalculatorParams) => {
  const total_info = getTotalInfo(item_card_list);
  const selected_zigzin_type_list = uniq(
    item_card_list.map((o) => checkZigzinOrVendorFlexDeliveryType(o.delivery_type)),
  );

  const has_matching_general = !!selected_zigzin_type_list.filter((o) => !o).length;
  const has_matching_zigzin = !!selected_zigzin_type_list.filter((o) => o).length;

  const is_matching_all = has_matching_zigzin && has_matching_general;

  const catalog_product = option_data_info?.catalog_product;
  const is_zigzin_product = checkZigzinOrVendorFlexFulfillmentType(catalog_product?.fulfillment_type ?? null);
  const coupon_discount_info_list = catalog_product?.product_price?.coupon_discount_info_list ?? [];

  // 최종 가격 계산
  const { final_price, zigzin_product_final_price } = coupon_discount_info_list.reduce(
    (previousValue, currentValue) => {
      previousValue.final_price = getDiscountedPrice({
        price_list: total_info.price_list,
        total_price: previousValue.final_price,
        coupon_discount_info: currentValue,
        first_order_discount: catalog_product?.product_price.first_order_discount,
        disabledCoupon,
      });

      previousValue.zigzin_product_final_price = getDiscountedPrice({
        price_list: total_info.zigzin_price_list,
        total_price: previousValue.zigzin_product_final_price,
        coupon_discount_info: currentValue,
        first_order_discount: catalog_product?.product_price.first_order_discount,
        disabledCoupon,
      });
      return previousValue;
    },
    {
      final_price: catalog_product?.store_deal_banner ? total_info.final_price : total_info.price,
      zigzin_product_final_price: catalog_product?.store_deal_banner ? total_info.final_price : total_info.zigzin_price,
    },
  );

  const has_coupon_discount =
    !!catalog_product?.product_price?.coupon_discount_info_list?.length && total_info.price >= final_price;

  const display_final_price = catalog_product?.product_price?.display_final_price;
  const final_discount_text = !disabledCoupon
    ? display_final_price?.final_price_additional?.badge?.text || display_final_price?.final_price?.badge?.text || ''
    : '';

  return {
    has_coupon_discount,
    is_digital_shipping: !catalog_product?.shipping_fee,

    total_info: {
      ...total_info,
      shipping_display: computeShippingDisplay({
        catalog_product: catalog_product ?? null,
        total_info,
        is_zigzin_product,
        is_matching_all,
        has_matching_zigzin,
        has_matching_general,
        zigzin_product_final_price,
        is_delivery_tomorrow: !zigzin_shipping_base_info?.oneday_shipping,
      }),
    },
    coupon_discount_amount: total_info.price - final_price,
    discount_color: option_data_info?.key_color?.discount_info_of_atf,

    final_price,
    final_price_color:
      display_final_price?.final_price_additional?.color?.normal ||
      display_final_price?.final_price?.color?.normal ||
      '',
    final_discount_text,
  };
};

/**
 * 배송비 표시 계산
 * @param catalog_product - 카탈로그 상품 정보
 * @param total_info - 총 가격 정보
 * @param is_zigzin_product - 직진 상품 여부
 * @param is_matching_all - 매칭 상품 여부
 * @param has_matching_zigzin - 직진 매칭 상품 여부
 * @param has_matching_general - 일반 매칭 상품 여부
 * @param zigzin_product_final_price - 직진 상품 최종 가격
 * @param is_delivery_tomorrow - 배송일 여부
 * @returns 배송비 표시 정보
 */
export const computeShippingDisplay = ({
  catalog_product,
  total_info,
  is_zigzin_product,
  is_matching_all,
  has_matching_zigzin,
  has_matching_general,
  zigzin_product_final_price,
  is_delivery_tomorrow,
}: {
  catalog_product: OptionInfo['catalog_product'] | null;
  total_info: TotalInfoProps;
  is_zigzin_product: boolean;
  is_matching_all: boolean;
  has_matching_zigzin: boolean;
  has_matching_general: boolean;
  zigzin_product_final_price: number;
  is_delivery_tomorrow: boolean;
}): ShippingDisplay => {
  // 현재 상품 및 매칭 상품의 배송비 정보
  const current_shipping_fee = catalog_product?.shipping_fee;
  const matching_product = catalog_product?.matching_catalog_product_info;
  const matched_shipping_fee = matching_product?.shipping_fee;

  if (!current_shipping_fee) {
    return { mode: 'hidden' };
  }
  if (current_shipping_fee.base_fee === null) {
    return { mode: 'text', text: '정보없음' };
  }

  // 무료배송 임계값 (현재/매칭)
  const free_shipping_threshold = Number(current_shipping_fee.minimum_free_shipping_fee ?? 0);
  const matched_free_shipping_threshold = Number(matched_shipping_fee?.minimum_free_shipping_fee ?? 0);

  const is_free_shipping_threshold = free_shipping_threshold > 0;
  const is_matched_free_shipping_threshold = matched_free_shipping_threshold > 0;

  // 상점/상품 정책으로 무료배송 여부
  const is_shop_free_shipping = current_shipping_fee.base_fee === 0;
  const is_product_free_shipping = current_shipping_fee.fee_type === PdpShippingFeeType.FREE;

  const is_zigzin_free_shipping =
    is_delivery_tomorrow || zigzin_product_final_price >= ZIGZIN_PRODUCT_FREE_SHIPPING_AMOUNT;

  const is_eligible_for_free_shipping_by_amount =
    (is_free_shipping_threshold && total_info.price >= free_shipping_threshold) ||
    (is_matched_free_shipping_threshold && total_info.price >= matched_free_shipping_threshold);

  const is_general_eligible_for_free_shipping_by_amount =
    (is_free_shipping_threshold && total_info.general_price >= free_shipping_threshold) ||
    (is_matched_free_shipping_threshold && total_info.general_price >= matched_free_shipping_threshold);

  const zigzin_shipping_fee_label = is_zigzin_free_shipping
    ? FREE_SHIPPING_AMOUNT_TEXT
    : ZIGZIN_PRODUCT_FREE_SHIPPING_AMOUNT_TEXT;

  const general_shipping_fee_label = `${asCurrency(matched_shipping_fee?.base_fee ?? 0)}${
    is_matched_free_shipping_threshold ? ` · ${asCurrency(matched_free_shipping_threshold)} 이상 무료` : ''
  }`;

  if (is_matching_all) {
    const general_shipping_fee =
      (is_zigzin_product ? matched_shipping_fee?.base_fee : current_shipping_fee.base_fee) ?? 0;
    const is_general_free = general_shipping_fee === 0 || is_general_eligible_for_free_shipping_by_amount;

    if (is_zigzin_free_shipping && is_general_free) {
      return { mode: 'text', text: FREE_SHIPPING_AMOUNT_TEXT };
    } else {
      return {
        mode: 'mixed',
        left_name: '직진',
        left_value: zigzin_shipping_fee_label,
        right_name: '일반',
        right_value: is_general_free ? FREE_SHIPPING_AMOUNT_TEXT : asCurrency(general_shipping_fee),
      };
    }
  }

  if (is_zigzin_product && matching_product && has_matching_zigzin) {
    return { mode: 'text', text: zigzin_shipping_fee_label };
  }

  if (is_zigzin_product && has_matching_general) {
    const is_free_matching_shop = matched_shipping_fee?.base_fee === 0;
    const is_free_matching_product = matched_shipping_fee?.fee_type === PdpShippingFeeType.FREE;
    const is_free_matching_shop_or_product = is_free_matching_shop || is_free_matching_product;

    if (is_free_matching_shop_or_product) {
      return { mode: 'text', text: '무료배송' };
    } else {
      return {
        mode: 'text',
        text: general_shipping_fee_label,
      };
    }
  }

  if (!is_zigzin_product && has_matching_zigzin) {
    return { mode: 'text', text: zigzin_shipping_fee_label };
  }

  if (is_shop_free_shipping || is_product_free_shipping || is_eligible_for_free_shipping_by_amount) {
    if (is_zigzin_product) {
      return is_zigzin_free_shipping
        ? { mode: 'text', text: FREE_SHIPPING_AMOUNT_TEXT }
        : { mode: 'text', text: ZIGZIN_PRODUCT_FREE_SHIPPING_AMOUNT_TEXT };
    }
    return { mode: 'text', text: FREE_SHIPPING_AMOUNT_TEXT };
  }

  return {
    mode: 'text',
    text: `배송비 ${asCurrency(current_shipping_fee.base_fee)}${
      is_free_shipping_threshold ? ` · ${asCurrency(free_shipping_threshold)} 이상 무료` : ''
    }`,
  };
};

/**
 * 총 가격 계산
 * @param items - 아이템 리스트
 * @returns 총 가격 정보
 */
const getTotalInfo = (items: ItemCardType[]): TotalInfoProps => {
  return items.reduce(
    (acc, { price, final_price, count, delivery_type }) => {
      const is_zigzin_option = checkZigzinOrVendorFlexDeliveryType(delivery_type);
      return {
        count: acc.count + count,
        price: acc.price + price * count,
        final_price: acc.final_price + final_price * count,
        price_list: acc.price_list.concat([{ price, count }]),
        general_price: acc.general_price + (is_zigzin_option ? 0 : price * count),
        zigzin_price_list: is_zigzin_option ? acc.zigzin_price_list.concat([{ price, count }]) : acc.zigzin_price_list,
        zigzin_count: acc.zigzin_count + (is_zigzin_option ? count : 0),
        zigzin_price: acc.zigzin_price + (is_zigzin_option ? price * count : 0),
      };
    },
    {
      count: 0,
      price: 0,
      final_price: 0,
      price_list: [] as PriceList,
      general_price: 0,
      zigzin_price_list: [] as PriceList,
      zigzin_count: 0,
      zigzin_price: 0,
    },
  );
};

/**
 * 할인 가격 계산
 * @param price_list - 가격 리스트
 * @param total_price - 총 가격
 * @param coupon_discount_info - 쿠폰 할인 정보
 * @param first_order_discount - 첫구매 할인 정보
 * @param disabledCoupon - 쿠폰 사용 여부
 * @returns 할인 가격
 */
export const getDiscountedPrice = ({
  price_list,
  total_price,
  coupon_discount_info,
  first_order_discount,
  disabledCoupon,
}: DiscountedPriceProps) => {
  if (disabledCoupon) return total_price;

  if (first_order_discount?.promotion_id) {
    return ceilTens(getFirstOrderDiscountPrice(total_price, price_list, first_order_discount));
  }

  const target_type = coupon_discount_info?.target_type;
  const min_required_amount = coupon_discount_info?.min_required_amount ?? 0;
  const is_more_than_coupon_min_require_amount = total_price >= min_required_amount;

  if (target_type && is_more_than_coupon_min_require_amount) {
    return ceilTens(getCouponDiscountPrice(total_price, price_list, coupon_discount_info));
  }
  return total_price;
};

/**
 * 쿠폰 할인 가격 계산
 * @param total_price - 총 가격
 * @param price_list - 가격 리스트
 * @param coupon_discount_info - 쿠폰 할인 정보
 * @returns 쿠폰 할인 가격
 */
const getCouponDiscountPrice = (
  total_price: number,
  price_list: PriceList,
  coupon_discount_info: ProductPriceCouponDiscountInfo | null,
) => {
  let discounted_price = 0;
  const target_type = coupon_discount_info?.target_type;
  const discount_type = coupon_discount_info?.discount_type;
  const discount_rate = bpToDecimal(coupon_discount_info?.discount_rate_bp ?? 0);
  const discount_amount_of_amount_coupon = coupon_discount_info?.discount_amount_of_amount_coupon ?? 0;
  const max_discount_amount = coupon_discount_info?.max_discount_amount ?? null;

  if (discount_type === PdpCouponDiscountType.RATE) {
    // MEMO: 정률 상품 그룹 쿠폰일 경우 가장 큰 금액에서 구한 할인 금액을 1회만 할인
    if (target_type === 'ORDER_ITEM') {
      const discountAmount = ceilTens(
        limitToMax(Math.max(...price_list.map((o) => o.price)) * discount_rate, max_discount_amount),
      );
      discounted_price = total_price - discountAmount;
    } else {
      const discount_amount = ceilTens(total_price * discount_rate);
      discounted_price = total_price - limitToMax(discount_amount, max_discount_amount);
    }
  }

  if (discount_type === PdpCouponDiscountType.AMOUNT) {
    const discount_amount = limitToMax(discount_amount_of_amount_coupon, max_discount_amount);
    discounted_price = total_price - discount_amount;
  }
  return getNonNegativeValue(discounted_price, total_price);
};

/**
 * 첫구매 할인 가격 계산
 * @param total_price - 총 가격
 * @param price_list - 가격 리스트
 * @param first_order_discount - 첫구매 할인 정보
 * @returns 첫구매 할인 가격
 */
const getFirstOrderDiscountPrice = (
  total_price: number,
  price_list: PriceList,
  first_order_discount: FirstOrderDiscount,
) => {
  if (first_order_discount.discount_type === PdpCouponDiscountType.RATE) {
    const discount_rate_bp = first_order_discount.discount_rate_bp ?? 0;
    const discount_amount = Math.max(...price_list.map((o) => o.price)) * bpToDecimal(discount_rate_bp);
    return getNonNegativeValue(total_price - discount_amount, total_price);
  }

  if (first_order_discount.discount_type === PdpCouponDiscountType.AMOUNT) {
    return getNonNegativeValue(total_price - first_order_discount.discount_amount, total_price);
  }

  return total_price;
};
